import type { DiaClient } from './client';
import { cacheRows, cachedRows } from './queries';
import type {
  Bed,
  BedType,
  ClaimedBy,
  Lang,
  MarketingListRow,
  Room,
  RoomClaim,
  RoomOccupancyRow,
  Uuid,
} from './types';

/**
 * Room Referee (migration 016): who sleeps where.
 *
 * Reads are network first and cache second, like every other read in this
 * package, so a room list opens on the boat. Writes are the exception in this
 * repo and deliberately so: a claim is first come first served, and a queued
 * claim that lands an hour later would hand somebody a bed that was taken in
 * the meantime. Every write here needs signal, says so in its error, and the
 * screens show an offline state rather than pretending.
 */

/* ------------------------------------------------------------------ */
/* Reads                                                               */
/* ------------------------------------------------------------------ */

type Loaded = { data: unknown; error: { message: string } | null };

async function readThrough<T extends { id: Uuid }>(
  entity: string,
  load: () => PromiseLike<Loaded>,
): Promise<T[]> {
  try {
    const { data, error } = await load();
    if (error) throw new Error(error.message);
    if (Array.isArray(data)) return cacheRows(entity, data as T[]);
  } catch {
    // Expected on the island. Answer from what the phone already has.
  }
  return cachedRows<T>(entity);
}

export function listBedTypes(client: DiaClient): Promise<BedType[]> {
  // bed_types is keyed by slug, not id, so it is cached under that key.
  return readThrough<BedType & { id: Uuid }>('bed_types', () =>
    client.from('bed_types').select('*').order('sort'),
  ).then((rows) => rows.map(({ slug, name_es, name_en, sleeps, sort, created_at, updated_at }) => ({
    slug,
    name_es,
    name_en,
    sleeps,
    sort,
    created_at,
    updated_at,
  })));
}

/** Every claim the caller may read. A guest gets their own stay and no other. */
export function listRoomClaims(client: DiaClient, stayId?: Uuid): Promise<RoomClaim[]> {
  const entity = stayId ? `room_claims:${stayId}` : 'room_claims';
  return readThrough<RoomClaim>(entity, () => {
    const query = client.from('room_claims').select('*');
    return stayId ? query.eq('stay_id', stayId) : query;
  });
}

/** The ops grid: a name beside a room number, behind the habitaciones module. */
export function listRoomOccupancy(client: DiaClient): Promise<RoomOccupancyRow[]> {
  return readThrough<RoomOccupancyRow>('room_occupancy', () =>
    client.from('room_occupancy').select('*'),
  );
}

/**
 * The opted in guests, for the owner console's CSV. Not cached: it is personal
 * data, it is read at a desk with signal, and a stale copy on a phone is the
 * last thing this list needs.
 */
export async function listMarketingList(client: DiaClient): Promise<MarketingListRow[]> {
  const { data, error } = await client
    .from('marketing_list')
    .select('*')
    .order('marketing_opt_in_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as MarketingListRow[];
}

/* ------------------------------------------------------------------ */
/* Writes, every one of which needs signal                             */
/* ------------------------------------------------------------------ */

export class NeedsSignalError extends Error {
  constructor() {
    super('needs_signal');
    this.name = 'NeedsSignalError';
  }
}

function requireOnline(): void {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new NeedsSignalError();
  }
}

export type ClaimRoomInput = {
  stayId: Uuid;
  roomId: Uuid;
  /** The person who will sleep there, who is not always the person tapping. */
  guestId: Uuid;
  claimedBy: ClaimedBy;
  claimedByGuestId?: Uuid | null;
  claimedByStaffId?: Uuid | null;
  note?: string | null;
};

/**
 * Takes a bed. The database refuses it if the room is full, if the room is not
 * a bedroom, if the person is not on the stay, or if the pick deadline has
 * passed and the caller is neither the booker nor staff. Those refusals are
 * the feature, so the message is handed back rather than swallowed.
 */
export async function claimRoom(client: DiaClient, input: ClaimRoomInput): Promise<RoomClaim> {
  requireOnline();
  const { data, error } = await client
    .from('room_claims')
    .insert({
      stay_id: input.stayId,
      room_id: input.roomId,
      guest_id: input.guestId,
      claimed_by: input.claimedBy,
      claimed_by_guest_id: input.claimedByGuestId ?? null,
      claimed_by_staff_id: input.claimedByStaffId ?? null,
      note: input.note ?? null,
    })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return data as RoomClaim;
}

/** Moves one person to another room. The same rules apply as to a fresh claim. */
export async function moveClaim(
  client: DiaClient,
  claimId: Uuid,
  roomId: Uuid,
  by: Pick<ClaimRoomInput, 'claimedBy' | 'claimedByGuestId' | 'claimedByStaffId'>,
): Promise<RoomClaim> {
  requireOnline();
  const { data, error } = await client
    .from('room_claims')
    .update({
      room_id: roomId,
      claimed_by: by.claimedBy,
      claimed_by_guest_id: by.claimedByGuestId ?? null,
      claimed_by_staff_id: by.claimedByStaffId ?? null,
    })
    .eq('id', claimId)
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return data as RoomClaim;
}

/** Gives the bed back. The audit row survives the claim. */
export async function releaseClaim(client: DiaClient, claimId: Uuid): Promise<void> {
  requireOnline();
  const { error } = await client.from('room_claims').delete().eq('id', claimId);
  if (error) throw new Error(error.message);
}

/** The booker's own stay, and only that one column. Null clears the deadline. */
export async function setRoomPickDeadline(
  client: DiaClient,
  deadline: string | null,
): Promise<void> {
  requireOnline();
  const { error } = await client.rpc('set_room_pick_deadline', { p_deadline: deadline });
  if (error) throw new Error(error.message);
}

/** The booker writes the party list. Names first, contact details optional. */
export type PartyMemberInput = {
  stayId: Uuid;
  firstName: string;
  lastName: string;
  email?: string | null;
  socialHandle?: string | null;
};

export async function addPartyMember(
  client: DiaClient,
  input: PartyMemberInput,
): Promise<{ id: Uuid }> {
  requireOnline();
  const { data, error } = await client
    .from('guests')
    .insert({
      stay_id: input.stayId,
      first_name: input.firstName,
      last_name: input.lastName,
      email: input.email ?? null,
      social_handle: input.socialHandle ?? null,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return data as { id: Uuid };
}

/**
 * The one Ops action that puts somebody on the marketing list.
 *
 * Nothing else does: an email address in the guests table is an address a
 * guest offered, not permission to write to them. Tim cleared the shape of
 * this with the attorney, and the database refuses a guest who tries to stamp
 * it for themselves.
 */
export async function recordMarketingOptIn(
  client: DiaClient,
  guestId: Uuid,
  staffUserId: Uuid | null,
): Promise<void> {
  requireOnline();
  const { error } = await client
    .from('guests')
    .update({ marketing_opt_in_at: new Date().toISOString(), marketing_opt_in_by: staffUserId })
    .eq('id', guestId);
  if (error) throw new Error(error.message);
}

/**
 * The two halves of a room's capacity, edited by an owner in Ops.
 *
 * max_sleeps is generated by the database from these two, so a ceiling that
 * disagrees with the beds is not a bug that can be written. The seeded numbers
 * are assumptions (see SESSIONS.md) and this is how somebody who has stood in
 * the room corrects them.
 */
export async function setRoomCapacity(
  client: DiaClient,
  roomId: Uuid,
  baseSleeps: number,
  extraSleeps: number,
): Promise<void> {
  requireOnline();
  if (!Number.isFinite(baseSleeps) || !Number.isFinite(extraSleeps)) {
    throw new Error('a capacity has to be a number');
  }
  if (baseSleeps < 0 || extraSleeps < 0) {
    throw new Error('a capacity cannot be negative');
  }
  const { error } = await client
    .from('rooms')
    .update({ base_sleeps: Math.round(baseSleeps), extra_sleeps: Math.round(extraSleeps) })
    .eq('id', roomId);
  if (error) throw new Error(error.message);
}

/** Takes somebody back off the list, for a guest who changes their mind. */
export async function clearMarketingOptIn(client: DiaClient, guestId: Uuid): Promise<void> {
  requireOnline();
  const { error } = await client
    .from('guests')
    .update({ marketing_opt_in_at: null, marketing_opt_in_by: null })
    .eq('id', guestId);
  if (error) throw new Error(error.message);
}

/* ------------------------------------------------------------------ */
/* Pure logic, shared by both shells                                   */
/* ------------------------------------------------------------------ */

export type RoomFill = {
  room: Room;
  /** How many people are in it right now. */
  taken: number;
  /** Beds still free, counting the futon. Never below zero. */
  free: number;
  full: boolean;
  /**
   * True once the room holds more people than its own beds sleep, which is the
   * moment housekeeping has to open the futon and make it up.
   */
  futonInUse: boolean;
};

/** How full one room is, given every claim on the stay. */
export function fillOf(room: Room, claims: Array<Pick<RoomClaim, 'room_id'>>): RoomFill {
  const taken = claims.filter((c) => c.room_id === room.id).length;
  return {
    room,
    taken,
    free: Math.max(0, room.max_sleeps - taken),
    full: taken >= room.max_sleeps,
    futonInUse: room.extra_sleeps > 0 && taken > room.base_sleeps,
  };
}

/** Every claimable room, in seed order, with how full each one is. */
export function fillEveryRoom(
  rooms: Room[],
  claims: Array<Pick<RoomClaim, 'room_id'>>,
): RoomFill[] {
  return rooms
    .filter((r) => r.claimable)
    .sort((a, b) => a.sort - b.sort || a.slug.localeCompare(b.slug))
    .map((room) => fillOf(room, claims));
}

export type PartyProgress = { placed: number; people: number; roomsUsed: number; rooms: number };

/** The one line the guest screen opens with: how far the group has got. */
export function progressOf(
  rooms: Room[],
  claims: Array<Pick<RoomClaim, 'room_id'>>,
  partySize: number,
): PartyProgress {
  const claimable = rooms.filter((r) => r.claimable);
  const used = new Set(claims.map((c) => c.room_id));
  return {
    placed: claims.length,
    people: Math.max(partySize, claims.length),
    roomsUsed: [...used].filter((id) => claimable.some((r) => r.id === id)).length,
    rooms: claimable.length,
  };
}

export type DeadlineState =
  | { kind: 'none' }
  | { kind: 'open'; at: Date; hoursLeft: number }
  | { kind: 'passed'; at: Date };

/** Where the stay stands against its optional pick deadline, right now. */
export function deadlineState(deadline: string | null, now: Date = new Date()): DeadlineState {
  if (!deadline) return { kind: 'none' };
  const at = new Date(deadline);
  if (Number.isNaN(at.getTime())) return { kind: 'none' };
  if (at.getTime() <= now.getTime()) return { kind: 'passed', at };
  return { kind: 'open', at, hoursLeft: Math.ceil((at.getTime() - now.getTime()) / 3_600_000) };
}

/**
 * The bed line on a room card, built from the row rather than from a join, so
 * it reads correctly with no signal. Returns one entry per bed line, already
 * in the reader's language, with the extra beds marked.
 */
export function describeBeds(
  beds: Bed[],
  bedTypes: BedType[],
  lang: Lang,
): Array<{ label: string; count: number; extra: boolean }> {
  return (beds ?? []).map((bed) => {
    const known = bedTypes.find((t) => t.slug === bed.type);
    const label = known ? (lang === 'en' ? known.name_en : known.name_es) : bed.type;
    return { label, count: bed.count, extra: bed.extra === true };
  });
}

/**
 * What the database will say if this claim is attempted, worked out on the
 * phone first so a person is told before they tap rather than after. The
 * database is still the fence: this only saves a round trip and a refusal.
 */
export type ClaimBlock = 'full' | 'deadline' | 'not_yours' | 'offline' | null;

export function whyNotClaim(input: {
  fill: RoomFill;
  deadline: DeadlineState;
  isBooker: boolean;
  forSelf: boolean;
  online: boolean;
}): ClaimBlock {
  if (!input.online) return 'offline';
  if (input.fill.full) return 'full';
  if (!input.forSelf && !input.isBooker) return 'not_yours';
  if (input.deadline.kind === 'passed' && !input.isBooker) return 'deadline';
  return null;
}

/* ------------------------------------------------------------------ */
/* CSV                                                                 */
/* ------------------------------------------------------------------ */

/**
 * RFC 4180 quoting: every field quoted, inner quotes doubled. A leading =, +,
 * - or @ is prefixed with a single quote so a spreadsheet opens the file as
 * text rather than as a formula.
 */
export function csvCell(value: unknown): string {
  const raw = value === null || value === undefined ? '' : String(value);
  const safe = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function toCsv(headers: string[], rows: Array<Array<unknown>>): string {
  const lines = [headers.map(csvCell).join(','), ...rows.map((row) => row.map(csvCell).join(','))];
  // CRLF, because this is opened in Excel on a laptop more often than not.
  return `${lines.join('\r\n')}\r\n`;
}

/** The owner console's export, header row included. */
export function marketingCsv(rows: MarketingListRow[]): string {
  return toCsv(
    [
      'first_name',
      'last_name',
      'email',
      'social_handle',
      'opted_in_at',
      'stay_code',
      'arrives_on',
      'departs_on',
    ],
    rows.map((r) => [
      r.first_name,
      r.last_name,
      r.email,
      r.social_handle,
      r.marketing_opt_in_at,
      r.stay_code,
      r.arrives_on,
      r.departs_on,
    ]),
  );
}
